import { Grid } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ContractStateDto, ContractStatusDto, NeedStatus, useListContractsQuery, useListNeedsQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useCreateNeed } from "../behov/useCreateNeed";
import { useCreatePublicSourcingEventModal } from "../../sourcing-events/buyer/public/useCreatePublicSourcingEventModal";
import type { HeroAction, HeroMetric } from "./homeConfig";
import { heroUsesMetric } from "./heroMetrics";
import { HeroActionCard } from "./HeroActionCard";
import { useHomeLabel } from "./useHomeLabel";

const CONTRACTS_COUNT_LIMIT = 100;

export const HeroActions = ({ actions }: { actions: HeroAction[] }) => {
  const { t } = useTranslation();
  const homeLabel = useHomeLabel();
  const navigate = useNavigate();
  const authState = useLoggedInWithOrgContextState();
  const { create: createNeed } = useCreateNeed();
  const { show: showCreateEvent, modal: createEventModal } = useCreatePublicSourcingEventModal();

  // Only fire a metric query when a rendered action actually needs it — the
  // REPORTED-needs query is advisor-facing and would 403 for the orderer.
  const wantsNeeds = heroUsesMetric(actions, "reportedNeeds");
  const wantsContracts = heroUsesMetric(actions, "activeContracts");
  const { data: reportedNeeds } = useListNeedsQuery(wantsNeeds ? { status: NeedStatus.Reported } : skipToken);
  const { data: activeContracts } = useListContractsQuery(
    wantsContracts
      ? {
          orgId: authState.selectedOrg.id,
          state: ContractStateDto.Published,
          // contracts' statuses is a single comma-joined string (ListContractsApiArg.statuses: string),
          // unlike events' statuses (string[]). Don't "normalize" to an array.
          statuses: [ContractStatusDto.Active, ContractStatusDto.Awarded].join(","),
          limit: CONTRACTS_COUNT_LIMIT,
        }
      : skipToken
  );

  const metricValue = (m?: HeroMetric): number | undefined => {
    if (m === "reportedNeeds") return reportedNeeds?.needs.length;
    if (m === "activeContracts") return activeContracts?.contracts.length;
    return undefined;
  };

  const sublabelFor = (a: HeroAction): string => {
    const v = metricValue(a.metric);
    if (v === undefined) return homeLabel(a.sublabelKey);
    if (a.metric === "reportedNeeds") return t("{{count}} reported needs waiting", { count: v });
    if (a.metric === "activeContracts")
      return v >= CONTRACTS_COUNT_LIMIT ? t("100+ active contracts") : t("{{count}} active contracts", { count: v });
    return homeLabel(a.sublabelKey);
  };

  const runTarget = (a: HeroAction) => {
    if (a.target.kind === "navigate") { navigate(a.target.to); return; }
    if (a.target.action === "createSourcingEvent") showCreateEvent();
    else void createNeed();
  };

  return (
    <>
      <Grid templateColumns={{ base: "1fr", md: "1.35fr 1fr 1fr" }} gap="3" mb="6">
        {actions.map((a) => (
          <HeroActionCard
            key={a.key}
            labelKey={a.labelKey}
            sublabel={sublabelFor(a)}
            icon={a.icon}
            primary={a.variant === "primary"}
            badge={a.metric === "reportedNeeds" ? metricValue("reportedNeeds") : undefined}
            onClick={() => runTarget(a)}
          />
        ))}
      </Grid>
      {createEventModal}
    </>
  );
};
